import { WebUiSyllabusTile } from '@/kontent/content-types'
import type { Mapping } from '@/types'
import { getFnSortYears, getLinkedItems } from '@/utils'
import { convertYearToNumeric } from '@/utils/stage_year'
import type { IContentItemsContainer } from '@kontent-ai/delivery-sdk'
import { useContext } from 'react'
import { WebsiteConfigurationContext } from '../contexts/WebsiteConfigurationProvider'
import SyllabusColumn from './SyllabusColumn'

export type SyllabusTableProps = {
	items: WebUiSyllabusTile[]
	linkedItems: IContentItemsContainer
	mappings: Mapping[]
	onClearFilter: () => void
	isFiltered: boolean
}

function SyllabusTable({
	items: syllabusTiles,
	linkedItems,
	mappings,
	onClearFilter,
	isFiltered,
}: SyllabusTableProps) {
	const { item: websiteConfiguration } =
		useContext(WebsiteConfigurationContext) || {}

	const leftColumnHeader =
		websiteConfiguration?.elements.kla_stage_page_left_column_title
			?.value || 'Current'
	const rightColumnHeader =
		websiteConfiguration?.elements.kla_stage_page_right_column_title
			?.value || 'Future'

	const isAllSyllabusRightEmpty = syllabusTiles.every(
		(syllabusTile) =>
			syllabusTile.elements.syllabuses_right.value.length === 0,
	)

	return (
		<div>
			{/* Header */}
			<div className="hidden lg:flex items-center border-b-2 pb-2.5 gap-4 border-[#22272B]">
				<div className="flex-1 font-bold">{leftColumnHeader}</div>
				{!isAllSyllabusRightEmpty && (
					<>
						<div className="w-[1px]"></div>
						<div className="flex-1 font-bold">
							{rightColumnHeader}
						</div>
					</>
				)}
			</div>
			{syllabusTiles.length === 0 && (
				<div className="flex flex-col md:flex-row items-center justify-center pt-[112px] pb-[48px] font-bold text-[20px] leading-7 px-4">
					We didn&rsquo;t find any results.&nbsp;
					<button
						type="button"
						className="text-[#002664] underline"
						onClick={() => onClearFilter()}
					>
						Clear all
					</button>
				</div>
			)}
			{/* Body */}
			{syllabusTiles
				.sort((a, b) => {
					// If title_blank is not empty, sort by title_blank, else sort by left column syllabuses title

					const aLeftSyllabuses = getLinkedItems(
						a.elements.syllabuses_left,
						linkedItems,
					)

					const bLeftSyllabuses = getLinkedItems(
						b.elements.syllabuses_left,
						linkedItems,
					)

					const aSortValue =
						a.elements.title_sort.value.length > 0
							? a.elements.title_sort.value
							: aLeftSyllabuses[0]?.elements.title.value || ''

					const bSortValue =
						b.elements.title_sort.value.length > 0
							? b.elements.title_sort.value
							: bLeftSyllabuses[0]?.elements.title.value || ''

					return aSortValue.localeCompare(bSortValue)
				})
				.sort((a, b) => {
					const sortFn = getFnSortYears()
					/**
					 * Sort by years using getFnSortYears
					 * If years are equal, sort by title
					 */

					const aYears =
						a.elements.stages__stage_years.value.sort(sortFn)
					const bYears =
						b.elements.stages__stage_years.value.sort(sortFn)
					// get the first year and last year of aYears and bYears
					const aFirstYear = convertYearToNumeric(
						aYears?.[0]?.codename,
					)
					const aLastYear = convertYearToNumeric(
						aYears?.[aYears.length - 1]?.codename,
					)
					const bFirstYear = convertYearToNumeric(
						bYears?.[0]?.codename,
					)
					const bLastYear = convertYearToNumeric(
						bYears?.[bYears.length - 1]?.codename,
					)
					const aYearsCompare = aFirstYear + aLastYear
					const bYearsCompare = bFirstYear + bLastYear

					// If it is a stage 6 taxonomy, sort to the end.
					// Year 11 syllabus should not be in front of Year 11-12 syllabuses.
					// All senior syllabuses are in the same stage group, ordered by title.
					if (aFirstYear === 11 || aFirstYear === 12) {
						return 0
					}
					return aYearsCompare - bYearsCompare
				})
				.map((syllabusTile) => {
					const titleBlank =
						syllabusTile.elements.title_blank.value || ''
					const leftSyllabuses = getLinkedItems(
						syllabusTile.elements.syllabuses_left,
						linkedItems,
					)

					const rightSyllabuses = getLinkedItems(
						syllabusTile.elements.syllabuses_right,
						linkedItems,
					)

					return (
						<div
							className="rounded lg:rounded-none p-4 lg:p-0 lg:py-4 flex flex-col lg:flex-row gap-4 items-stretch border border-[#CDD3D6] lg:border-0 lg:border-b lg:border-[#22272B] mb-6 last:mb-0 lg:mb-0"
							key={syllabusTile.system.id}
							data-id={syllabusTile.system.id}
						>
							{/* Left column */}
							<SyllabusColumn
								header={leftColumnHeader}
								syllabuses={leftSyllabuses}
								tooltipElement={
									syllabusTile.elements.syllabus_tooltip_left
								}
								linkedItems={linkedItems}
								mappings={mappings}
								isFiltered={isFiltered}
								titleBlank={titleBlank}
							/>

							{/* Right column */}
							{!isAllSyllabusRightEmpty && (
								<>
									<div className="bg-[#CDD3D6] w-full h-[1px] lg:h-auto lg:w-[1px] block"></div>
									<SyllabusColumn
										header={rightColumnHeader}
										syllabuses={rightSyllabuses}
										tooltipElement={
											syllabusTile.elements
												.syllabus_tooltip_right
										}
										linkedItems={linkedItems}
										mappings={mappings}
										isFiltered={isFiltered}
										titleBlank={titleBlank}
									/>
								</>
							)}
						</div>
					)
				})}
		</div>
	)
}

export default SyllabusTable
